import { AssetActionRow } from '@/components/AssetActionRow';
import { useBulkSelectionContext } from '@/components/ContentsTable/BulkSelectionContext';
import { Ellipsis } from '@/components/Ellipsis';
import { Thumbnail } from '@/components/Thumbnail';
import { MatchAsset } from '@/components/Search/SearchTable';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetFileName, getAssetFolderDirectory, getAssetName } from '@/utils/assets';
import { dateFormat, formatMatchPercentage } from '@/utils/format';
import { Box, Card, Chip, Tooltip, Typography, styled } from '@mui/material';
import { getFileExtension } from '@physna/supported-files';
import { Link } from 'react-router-dom';
import { MediaArea, StyledCardContent } from './cardStyles';

const ROOT_FOLDER_NAME = 'Home';

// The card has room for the name and nothing else, so where the asset lives is named on hover.
const buildCardTooltip = (asset: MatchAsset) => {
  const folder = getAssetFolderDirectory(asset) || ROOT_FOLDER_NAME;
  const added = asset.createdAt ? `, added ${dateFormat(asset.createdAt)}` : '';
  return `${getAssetFileName(asset)} in ${folder}${added}`;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
}));

const MatchPill = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const SearchResultCard = ({
  asset,
  onContextMenu,
  isCrossTenant = false,
}: {
  asset: MatchAsset;
  onContextMenu?: (event: React.MouseEvent) => void;
  isCrossTenant?: boolean;
}) => {
  const { getPath } = useTenants();
  const bulkSelection = useBulkSelectionContext<MatchAsset>();
  const extension = getFileExtension(asset.path);

  return (
    <StyledCard data-id={asset.id} data-field='name' onContextMenu={onContextMenu}>
      <StyledLink to={getPath(`asset/${asset.id}`)}>
        <Tooltip title={buildCardTooltip(asset)} arrow placement='top'>
          <MediaArea data-testid='media-area'>
            <Thumbnail asset={asset} size={160} noLink />
            {asset.matchPercentage !== undefined && (
              <MatchPill label={`${formatMatchPercentage(asset.matchPercentage)}%`} size='small' />
            )}
          </MediaArea>
        </Tooltip>
        <StyledCardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant='body2'
              component='span'
              sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', minWidth: 0 }}
            >
              <Ellipsis text={getAssetName(asset)} />
            </Typography>
            {extension && <Chip label={extension} size='small' variant='outlined' sx={{ flexShrink: 0 }} />}
          </Box>
        </StyledCardContent>
      </StyledLink>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', px: 1.5, py: 0.5 }}>
        <AssetActionRow
          asset={asset}
          hideAssetPageLink
          showReprocess
          collapseExtraActions
          readOnly={isCrossTenant}
          onDelete={() => bulkSelection?.onItemsDeleted?.([asset.id])}
          slim
          sx={{
            justifyContent: 'center',
            '& > *': { display: 'inline-flex', alignItems: 'center' },
            '& .MuiIconButton-root': { padding: '6px' },
            '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
          }}
        />
      </Box>
    </StyledCard>
  );
};
